import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

/**
 * Splits a video into individual image frames using FFmpeg WASM
 * @param videoFile - The video file to split (File or Blob)
 * @param options - Optional configuration
 * @returns Array of image blobs representing each frame
 */
export async function splitVideoFrames(
  videoFile: File | Blob,
  options?: {
    fps?: number; // Frames per second to extract (default: extract all frames)
    discardFirstFrame?: boolean; // Whether to remove the first extracted frame
  }
): Promise<Blob[]> {
  const { fps, discardFirstFrame } = options || {};

  // Initialize FFmpeg
  const ffmpeg = new FFmpeg();

  // Determine if multithreading is supported based on cross-origin isolation
  const isMultithreaded = typeof window !== 'undefined' && window.crossOriginIsolated;
  const coreVersion = isMultithreaded ? 'core-mt' : 'core';
  const baseURL = `https://unpkg.com/@ffmpeg/${coreVersion}@0.12.6/dist/umd`;

  // console.log(`Loading FFmpeg (${isMultithreaded ? 'Multi-threaded' : 'Single-threaded'})...`);

  // Load FFmpeg core
  await ffmpeg.load({
    coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
    wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    // workerURL is only required for the multithreaded version
    ...(isMultithreaded && {
      workerURL: await toBlobURL(`${baseURL}/ffmpeg-core.worker.js`, 'text/javascript'),
    }),
  });

  try {
    // Write input video to FFmpeg's virtual file system
    const inputFileName = 'input_video';
    await ffmpeg.writeFile(inputFileName, await fetchFile(videoFile));

    // Build FFmpeg command as an array of strings
    const outputPattern = `frame_%04d.png`;
    const args: string[] = [];

    // if (format === 'png') {
    //   args.push('-c:v', 'libvpx-vp9');
    // }

    // Global / Input options
    args.push('-i', inputFileName);

    // Filter options
    if (fps) {
      args.push('-vf', `fps=${fps}`);
    }

    // Output options
    args.push('-pix_fmt', 'rgba');

    // Output file pattern
    args.push(outputPattern);

    // Execute FFmpeg command
    await ffmpeg.exec(args);

    // Read all generated frame files
    const frames: Blob[] = [];
    let frameIndex = 1;

    while (true) {
      const frameFileName = `frame_${frameIndex.toString().padStart(4, '0')}.png`;
      
      try {
        const frameData = await ffmpeg.readFile(frameFileName);
        
        // Convert FileData to Uint8Array and then to Blob
        const mimeType = 'image/png';
        const uint8Array = new Uint8Array(frameData as Uint8Array);
        const blob = new Blob([uint8Array], { type: mimeType });
        frames.push(blob);
        
        frameIndex++;
      } catch (error) {
        // No more frames found in virtual FS
        break;
      }
    }

    if ((discardFirstFrame) && frames.length === 49) { // 49 frames is exactly the number of frames for generate animation
      // console.log('Discarding first frame');
      frames.shift(); // Remove the first frame
    }

    return frames;
  } finally {
    // Explicitly terminate to free resources
    await ffmpeg.terminate();
  }
}